import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { 
  Users, 
  Award, 
  Globe, 
  Target, 
  Heart, 
  Lightbulb,
  Shield,
  TrendingUp,
  CheckCircle,
  Star
} from 'lucide-react';

const AboutPage = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const stats = [
    {
      icon: <Users className="w-8 h-8" />,
      value: '500+',
      label: language === 'en' ? 'Happy Clients' : 'Clientes Satisfechos',
      description: language === 'en' ? 'Across Mexico and Latin America' : 'En México y Latinoamérica'
    },
    {
      icon: <Award className="w-8 h-8" />,
      value: '10+',
      label: language === 'en' ? 'Years Experience' : 'Años de Experiencia',
      description: language === 'en' ? 'In business management software' : 'En software de gestión empresarial'
    },
    {
      icon: <Globe className="w-8 h-8" />,
      value: '4',
      label: language === 'en' ? 'Industries Served' : 'Industrias Atendidas',
      description: language === 'en' ? 'Specialized solutions' : 'Soluciones especializadas'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      value: '98%',
      label: language === 'en' ? 'Success Rate' : 'Tasa de Éxito',
      description: language === 'en' ? 'Client satisfaction' : 'Satisfacción del cliente'
    }
  ];

  const values = [
    {
      icon: <Target className="w-12 h-12" />,
      title: language === 'en' ? 'Excellence' : 'Excelencia',
      description: language === 'en' 
        ? 'We strive for perfection in every solution we deliver, ensuring our clients receive world-class software that exceeds expectations.'
        : 'Nos esforzamos por la perfección en cada solución que entregamos, asegurando que nuestros clientes reciban software de clase mundial que supere las expectativas.'
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: language === 'en' ? 'Client-Centric' : 'Centrados en el Cliente',
      description: language === 'en'
        ? 'Our clients\' success is our success. We build lasting partnerships based on trust, transparency, and mutual growth.'
        : 'El éxito de nuestros clientes es nuestro éxito. Construimos asociaciones duraderas basadas en confianza, transparencia y crecimiento mutuo.'
    },
    {
      icon: <Lightbulb className="w-12 h-12" />,
      title: language === 'en' ? 'Innovation' : 'Innovación',
      description: language === 'en'
        ? 'We continuously evolve our platform with cutting-edge technology to keep our clients ahead of the competition.'
        : 'Evolucionamos continuamente nuestra plataforma con tecnología de vanguardia para mantener a nuestros clientes por delante de la competencia.'
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: language === 'en' ? 'Reliability' : 'Confiabilidad',
      description: language === 'en'
        ? 'Our robust, secure platform ensures 99.9% uptime and enterprise-grade security for peace of mind.'
        : 'Nuestra plataforma robusta y segura asegura 99.9% de tiempo activo y seguridad de nivel empresarial para tranquilidad.'
    }
  ];

  const team = [
    {
      name: 'Carlos Mendoza',
      role: language === 'en' ? 'CEO & Founder' : 'CEO y Fundador',
      description: language === 'en' 
        ? '15+ years in enterprise software, former Microsoft executive'
        : '15+ años en software empresarial, ex-ejecutivo de Microsoft',
      image: 'https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1'
    },
    {
      name: 'Ana Rodriguez',
      role: language === 'en' ? 'CTO' : 'Directora de Tecnología',
      description: language === 'en'
        ? 'Expert in cloud architecture and AI integration'
        : 'Experta en arquitectura en la nube e integración de IA',
      image: 'https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1'
    },
    {
      name: 'Miguel Santos',
      role: language === 'en' ? 'VP of Sales' : 'VP de Ventas',
      description: language === 'en'
        ? 'Industry veteran with deep knowledge of Mexican market'
        : 'Veterano de la industria con conocimiento profundo del mercado mexicano',
      image: 'https://images.pexels.com/photos/3785077/pexels-photo-3785077.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={language === 'en' ? 'About LAIMU - Leading Business Software Company in Mexico' : 'Acerca de LAIMU - Empresa Líder de Software Empresarial en México'}
        description={language === 'en' ? 'Learn about LAIMU Trantor, Mexico\'s leading provider of integrated business management solutions. 10+ years of experience serving 500+ companies.' : 'Conoce LAIMU Trantor, el proveedor líder de México en soluciones integradas de gestión empresarial. 10+ años de experiencia sirviendo a 500+ empresas.'}
        url="/about"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
              <Heart className="w-4 h-4 mr-2" />
              {language === 'en' ? 'Our Story' : 'Nuestra Historia'}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {language === 'en' ? 'Transforming Business Since 2015' : 'Transformando Negocios Desde 2015'}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {language === 'en' 
                ? 'Born in Mexico City with a vision to revolutionize how businesses operate. Today, we\'re the trusted partner for 500+ companies across Latin America.'
                : 'Nacimos en la Ciudad de México con la visión de revolucionar cómo operan los negocios. Hoy, somos el socio de confianza para más de 500 empresas en Latinoamérica.'
              }
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e0f2fe' }}>
                  <span style={{ color: '#115d7b' }}>
                    {stat.icon}
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="font-semibold text-gray-900 mb-1">{stat.label}</div>
                <div className="text-gray-600 text-sm">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#115d7b' }}>
                <Target className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {language === 'en' ? 'Our Mission' : 'Nuestra Misión'}
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                {language === 'en'
                  ? 'To empower businesses across Latin America with world-class integrated software solutions that drive efficiency, growth, and success. We believe every company deserves access to enterprise-grade technology.'
                  : 'Empoderar a las empresas de Latinoamérica con soluciones de software integradas de clase mundial que impulsen eficiencia, crecimiento y éxito. Creemos que toda empresa merece acceso a tecnología de nivel empresarial.'
                }
              </p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-8 rounded-2xl">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#ff8c00' }}>
                <Lightbulb className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {language === 'en' ? 'Our Vision' : 'Nuestra Visión'}
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                {language === 'en'
                  ? 'To be the leading business management platform in Latin America, recognized for innovation, reliability, and exceptional customer success. We envision a future where every business operates at peak efficiency.'
                  : 'Ser la plataforma líder de gestión empresarial en Latinoamérica, reconocida por innovación, confiabilidad y éxito excepcional del cliente. Visualizamos un futuro donde cada negocio opere con máxima eficiencia.'
                }
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Our Core Values' : 'Nuestros Valores Fundamentales'}
            </h2>
            <p className="text-xl text-gray-600">
              {language === 'en' 
                ? 'The principles that guide everything we do and every decision we make.'
                : 'Los principios que guían todo lo que hacemos y cada decisión que tomamos.'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-start mb-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mr-6 flex-shrink-0" style={{ backgroundColor: '#e0f2fe' }}>
                    <span style={{ color: '#115d7b' }}>
                      {value.icon}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Meet Our Leadership' : 'Conoce Nuestro Liderazgo'}
            </h2>
            <p className="text-xl text-gray-600">
              {language === 'en' 
                ? 'Experienced leaders driving innovation and excellence in business software.'
                : 'Líderes experimentados impulsando innovación y excelencia en software empresarial.'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                  <p className="font-medium mb-3" style={{ color: '#115d7b' }}>{member.role}</p>
                  <p className="text-gray-600">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Homogenized Design */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg text-center">
              <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
                <Star className="w-4 h-4 mr-2" />
                {language === 'en' ? 'Join Our Success Story' : 'Únete a Nuestra Historia de Éxito'}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {language === 'en' ? 'Ready to Transform Your Business?' : '¿Listo para Transformar Tu Negocio?'}
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {language === 'en' 
                  ? 'Discover how LAIMU Trantor can transform your business operations and drive unprecedented growth. Join hundreds of companies that have already revolutionized their operations.'
                  : 'Descubre cómo LAIMU Trantor puede transformar las operaciones de tu negocio e impulsar un crecimiento sin precedentes. Únete a cientos de empresas que ya han revolucionado sus operaciones.'
                }
              </p>
              
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2" style={{ color: '#115d7b' }}>500+</div>
                  <div className="text-gray-600 text-sm">
                    {language === 'en' ? 'Companies' : 'Empresas'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2" style={{ color: '#ff8c00' }}>98%</div>
                  <div className="text-gray-600 text-sm">
                    {language === 'en' ? 'Satisfaction' : 'Satisfacción'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2" style={{ color: '#10b981' }}>45%</div>
                  <div className="text-gray-600 text-sm">
                    {language === 'en' ? 'ROI Increase' : 'Aumento ROI'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2" style={{ color: '#8b5cf6' }}>6</div>
                  <div className="text-gray-600 text-sm">
                    {language === 'en' ? 'Months to ROI' : 'Meses a ROI'}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/demo"
                  className="px-8 py-4 text-white rounded-lg transition-all duration-300 font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  style={{ backgroundColor: '#115d7b' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
                >
                  {language === 'en' ? 'Schedule Demo' : 'Agendar Demo'}
                </a>
                <a
                  href="/success-cases"
                  className="px-8 py-4 border-2 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300 font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  style={{ borderColor: '#115d7b' }}
                >
                  {language === 'en' ? 'View Success Stories' : 'Ver Casos de Éxito'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;